import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import BetaSignupSection from '../components/BetaSignupSection';
import ReferralTab from '../components/ReferralTab';

const API = 'https://v-room.app/api';

function mapProfile(u) {
  return {
    id:            u.userId       ?? u.id,
    username:      u.username,
    email:         u.email        ?? null,
    location:      u.location     ?? null,
    bio:           u.bio          ?? null,
    avatarUrl:     u.avatarUrl    ?? u.avatar ?? null,
    createdAt:     u.createdAt    ?? null,
    totalDistance: u.totalDistance ?? 0,
    topSpeed:      u.topSpeed      ?? 0,
    points:        u.points        ?? 0,
    meetCount:     u.meetCount     ?? 0,
    cityCount:     u.cityCount     ?? 0,
    position:      u.position      ?? null,
    spotCount:     u.spotCount     ?? 0,
    achievementCount: u.achievementCount ?? 0,
    monthlyDistance:  u.monthlyDistance  ?? 0,
    weeklyDistance:   u.weeklyDistance   ?? 0,
    totalRides:       u.totalRides       ?? 0,
    streak:           u.streak           ?? 0,
    mainCar:          u.mainCar          ?? null,
    latestAchievement: u.latestAchievement ?? null,
  };
}

// ── Zakładka: Statystyki ──────────────────────────────────────────────────────
function StatsTab({ profile }) {
  const stats = [
    { icon: 'route',          label: 'ŁĄCZNY DYSTANS',    value: `${Number(profile.totalDistance).toFixed(0)} km` },
    { icon: 'calendar_today', label: 'DYSTANS W TYM MIES.', value: `${Number(profile.monthlyDistance).toFixed(0)} km` },
    { icon: 'date_range',     label: 'DYSTANS W TYM TYG.',  value: `${Number(profile.weeklyDistance).toFixed(0)} km` },
    { icon: 'speed',          label: 'MAKS. PRĘDKOŚĆ',    value: `${profile.topSpeed} km/h` },
    { icon: 'flag',           label: 'LICZBA TRAS',       value: String(profile.totalRides) },
    { icon: 'local_fire_department', label: 'STREAK',     value: `${profile.streak} dni` },
    { icon: 'star',           label: 'PUNKTY',            value: profile.points.toLocaleString('pl-PL') },
    { icon: 'military_tech',  label: 'OSIĄGNIĘCIA',       value: String(profile.achievementCount) },
    { icon: 'location_on',    label: 'ODWIEDZONE MIASTA', value: String(profile.cityCount) },
    { icon: 'people',         label: 'SPOTY',             value: String(profile.spotCount) },
  ];

  return (
    <div className="tab-stats-grid">
      {stats.map(s => (
        <div key={s.label} className="tab-stat-card">
          <span className="material-icons">{s.icon}</span>
          <div className="tab-stat-value">{s.value}</div>
          <div className="tab-stat-label">{s.label}</div>
        </div>
      ))}
      {profile.mainCar && (
        <div className="tab-stat-card tab-main-car">
          <span className="material-icons">directions_car</span>
          <div className="tab-stat-value">{profile.mainCar.brand}</div>
          <div className="tab-stat-label">{profile.mainCar.specs}</div>
        </div>
      )}
      {profile.latestAchievement && (
        <div className="tab-stat-card tab-achievement">
          <span style={{ fontSize: 28 }}>{profile.latestAchievement.icon}</span>
          <div className="tab-stat-value">{profile.latestAchievement.label}</div>
          <div className="tab-stat-label">OSTATNIE OSIĄGNIĘCIE</div>
        </div>
      )}
    </div>
  );
}

// ── Zakładka: Garaż ───────────────────────────────────────────────────────────
function CarsTab({ userId, token }) {
  const [cars,       setCars]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [showForm,   setShowForm]   = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting,   setDeleting]   = useState(null);
  const [msg,        setMsg]        = useState(null);

  // pola formularza
  const [brand,   setBrand]   = useState('');
  const [model,   setModel]   = useState('');
  const [year,    setYear]    = useState('');
  const [power,   setPower]   = useState('');
  const [engine,  setEngine]  = useState('');
  const [color,   setColor]   = useState('');
  const [mods,    setMods]    = useState('');
  const [isMain,  setIsMain]  = useState(false);
  const [photos,   setPhotos]   = useState([]);
  const [previews, setPreviews] = useState([]);
  const fileRef = useRef();

  const fetchCars = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/profile/${userId}/cars`);
      const data = await res.json();
      setCars(Array.isArray(data) ? data : []);
    } catch {}
    finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { fetchCars(); }, [fetchCars]);

  // specs złożone z pól — tak samo jak w apce mobilnej
  const buildSpecs = () =>
    [year && `${year} r.`, power && `${power} KM`, engine, color]
      .filter(Boolean).join(' · ') || `${brand} ${model}`.trim();

  const handlePhotos = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const newPhotos   = [...photos,   ...files].slice(0, 5);
    const newPreviews = [...previews, ...files.map(f => URL.createObjectURL(f))].slice(0, 5);
    setPhotos(newPhotos);
    setPreviews(newPreviews);
  };

  const removePhoto = (i) => {
    URL.revokeObjectURL(previews[i]);
    setPhotos(p   => p.filter((_, idx) => idx !== i));
    setPreviews(p => p.filter((_, idx) => idx !== i));
  };

  const resetForm = () => {
    setBrand(''); setModel(''); setYear(''); setPower('');
    setEngine(''); setColor(''); setMods(''); setIsMain(false);
    previews.forEach(p => URL.revokeObjectURL(p));
    setPhotos([]); setPreviews([]);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!brand.trim()) { setMsg({ type: 'error', text: 'Podaj markę auta' }); return; }
    if (!model.trim()) { setMsg({ type: 'error', text: 'Podaj model auta' }); return; }
    setSubmitting(true); setMsg(null);
    try {
      const form = new FormData();
      form.append('brand',  `${brand.trim()} ${model.trim()}`);
      form.append('specs',  buildSpecs());
      form.append('isMain', String(isMain));
      if (mods.trim()) form.append('mods', mods.trim());
      photos.forEach(f => form.append('photos', f));

      const res  = await fetch(`${API}/cars`, {
        method:  'POST',
        headers: { Authorization: `Bearer ${token}` },
        body:    form,
      });
      const data = await res.json();

      if (res.ok) {
        setMsg({ type: 'success', text: `🚗 ${brand} ${model} dodane do garażu!` });
        resetForm();
        fetchCars();
      } else {
        setMsg({ type: 'error', text: data.error || 'Błąd dodawania auta' });
      }
    } catch {
      setMsg({ type: 'error', text: 'Błąd połączenia z serwerem' });
    } finally {
      setSubmitting(false);
      setTimeout(() => setMsg(null), 5000);
    }
  };

  const handleSetMain = async (carId) => {
    try {
      await fetch(`${API}/cars/${carId}/main`, {
        method: 'PATCH', headers: { Authorization: `Bearer ${token}` },
      });
      fetchCars();
    } catch {}
  };

  const handleDelete = async (carId) => {
    if (!window.confirm('Usunąć to auto z garażu?')) return;
    setDeleting(carId);
    try {
      await fetch(`${API}/cars/${carId}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      setCars(prev => prev.filter(c => c.id !== carId));
    } catch {}
    finally { setDeleting(null); }
  };

  if (loading) return <div className="tab-loading"><div className="profile-spinner"></div></div>;

  return (
    <div className="cars-tab">

      {/* PRZYCISK DODAJ */}
      {!showForm && (
        <button className="btn-add-car" onClick={() => setShowForm(true)}>
          <span className="material-icons">add</span>
          Dodaj auto do garażu
        </button>
      )}

      {/* FORMULARZ */}
      {showForm && (
        <form className="add-car-form" onSubmit={handleSubmit}>

          <div className="add-car-form-header">
            <h3><span className="material-icons">directions_car</span>DODAJ AUTO</h3>
            <button type="button" className="btn-form-close" onClick={resetForm}>
              <span className="material-icons">close</span>
            </button>
          </div>

          {/* ZDJĘCIA */}
          <div className="add-car-field">
            <label>ZDJĘCIA ({previews.length}/5)</label>
            <div className="add-car-photos-grid">
              {previews.map((src, i) => (
                <div key={i} className="add-car-photo-preview">
                  <img src={src} alt={`foto ${i}`} />
                  <button type="button" className="add-car-photo-remove" onClick={() => removePhoto(i)}>
                    <span className="material-icons">close</span>
                  </button>
                </div>
              ))}
              {previews.length < 5 && (
                <button type="button" className="add-car-photo-add" onClick={() => fileRef.current?.click()}>
                  <span className="material-icons">add_photo_alternate</span>
                  <span>Dodaj</span>
                </button>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple
              style={{ display: 'none' }} onChange={handlePhotos} />
          </div>

          {/* MARKA + MODEL obok siebie */}
          <div className="add-car-row">
            <div className="add-car-field">
              <label>MARKA *</label>
              <div className="add-car-input-wrap">
                <span className="material-icons">directions_car</span>
                <input type="text" placeholder="np. BMW, Mercedes..."
                  value={brand} onChange={e => setBrand(e.target.value)}
                  maxLength={40} required />
              </div>
            </div>
            <div className="add-car-field">
              <label>MODEL *</label>
              <div className="add-car-input-wrap">
                <span className="material-icons">label</span>
                <input type="text" placeholder="np. M4 Competition..."
                  value={model} onChange={e => setModel(e.target.value)}
                  maxLength={40} required />
              </div>
            </div>
          </div>

          {/* ROK + MOC obok siebie */}
          <div className="add-car-row">
            <div className="add-car-field">
              <label>ROK</label>
              <div className="add-car-input-wrap">
                <span className="material-icons">calendar_today</span>
                <input type="number" placeholder="2024"
                  value={year} onChange={e => setYear(e.target.value)}
                  min={1900} max={2099} />
              </div>
            </div>
            <div className="add-car-field">
              <label>MOC (KM)</label>
              <div className="add-car-input-wrap">
                <span className="material-icons">speed</span>
                <input type="number" placeholder="510"
                  value={power} onChange={e => setPower(e.target.value)}
                  min={1} max={5000} />
              </div>
            </div>
          </div>

          {/* SILNIK + KOLOR obok siebie */}
          <div className="add-car-row">
            <div className="add-car-field">
              <label>SILNIK</label>
              <div className="add-car-input-wrap">
                <span className="material-icons">settings</span>
                <input type="text" placeholder="np. 3.0 TwinTurbo..."
                  value={engine} onChange={e => setEngine(e.target.value)}
                  maxLength={40} />
              </div>
            </div>
            <div className="add-car-field">
              <label>KOLOR</label>
              <div className="add-car-input-wrap">
                <span className="material-icons">palette</span>
                <input type="text" placeholder="np. Czarny mat..."
                  value={color} onChange={e => setColor(e.target.value)}
                  maxLength={40} />
              </div>
            </div>
          </div>

          {/* MODYFIKACJE */}
          <div className="add-car-field">
            <label>MODYFIKACJE</label>
            <div className="add-car-input-wrap add-car-textarea-wrap">
              <span className="material-icons">build</span>
              <textarea placeholder="np. Stage 2 tune, exhaust, coilovers..."
                value={mods} onChange={e => setMods(e.target.value)}
                maxLength={300} rows={3} />
            </div>
            <span className="add-car-char">{mods.length}/300</span>
          </div>

          {/* GŁÓWNE AUTO */}
          <div className="add-car-switch-row">
            <div>
              <div className="add-car-switch-label">USTAW JAKO GŁÓWNE</div>
              <div className="add-car-switch-sub">Wyświetlane na Twoim profilu</div>
            </div>
            <button
              type="button"
              className={`add-car-toggle ${isMain ? 'on' : ''}`}
              onClick={() => setIsMain(v => !v)}
            >
              <div className="add-car-toggle-knob"></div>
            </button>
          </div>

          {/* PODGLĄD — tylko gdy wypełnione pola */}
          {(brand || model || year || power || engine) && (
            <div className="add-car-preview">
              <div className="add-car-preview-label">PODGLĄD</div>
              <div className="add-car-preview-brand">
                {[brand, model].filter(Boolean).join(' ') || '—'}
              </div>
              <div className="add-car-preview-specs">
                {[year && `${year} r.`, power && `${power} KM`, engine, color]
                  .filter(Boolean).join(' · ') || '—'}
              </div>
            </div>
          )}

          {msg && <div className={`edit-msg ${msg.type}`}>{msg.text}</div>}

          <div className="add-car-btns">
            <button type="submit" className="btn-save-profile" disabled={submitting}>
              {submitting
                ? <><div className="btn-spinner"></div>Dodawanie...</>
                : <><span className="material-icons">garage</span>DODAJ AUTO</>
              }
            </button>
            <button type="button" className="btn-cancel-car" onClick={resetForm}>
              Anuluj
            </button>
          </div>

        </form>
      )}

      {/* LISTA AUT */}
      {cars.length === 0 && !showForm ? (
        <div className="profile-empty">
          <span className="material-icons">directions_car</span>
          <p>Garaż jest pusty</p>
          <span>Dodaj swoje pierwsze auto!</span>
        </div>
      ) : (
        <div className="cars-grid">
          {cars.map(car => (
            <div key={car.id} className={`car-card ${car.isMain ? 'car-main' : ''}`}>
              {car.photos?.[0]
                ? <img src={car.photos[0]} alt={car.brand} className="car-photo" />
                : (
                  <div className="car-photo-placeholder">
                    <span className="material-icons">directions_car</span>
                  </div>
                )
              }
              <div className="car-info">
                {car.isMain && <span className="car-main-badge">⭐ GŁÓWNE</span>}
                <div className="car-brand">{car.brand}</div>
                <div className="car-specs">{car.specs}</div>
                <div className="car-actions">
                  {!car.isMain && (
                    <button className="car-action-btn car-btn-main"
                      onClick={() => handleSetMain(car.id)}>
                      <span className="material-icons">star</span>
                      Ustaw główne
                    </button>
                  )}
                  <button className="car-action-btn car-btn-delete"
                    onClick={() => handleDelete(car.id)}
                    disabled={deleting === car.id}>
                    {deleting === car.id
                      ? <div className="btn-spinner" style={{ width: 14, height: 14 }}></div>
                      : <span className="material-icons">delete</span>
                    }
                    Usuń
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

    </div>
  );
}

// ── Zakładka: Osiągnięcia ────────────────────────────────────────────────���────
function AchievementsTab({ userId }) {
  const [achievements, setAchievements] = useState([]);
  const [loading,      setLoading]      = useState(true);

  const RARITY_COLOR = { legendary: '#f5c518', epic: '#a338e3', rare: '#38a5e3', common: '#ffffff50' };

  useEffect(() => {
    fetch(`${API}/profile/${userId}/achievements`)
      .then(r => r.json())
      .then(data => { setAchievements(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [userId]);

  if (loading) return <div className="tab-loading"><div className="profile-spinner"></div></div>;

  if (achievements.length === 0) return (
    <div className="profile-empty">
      <span className="material-icons">military_tech</span>
      <p>Brak osiągnięć</p>
      <span>Osiągnięcia odblokowujesz w aplikacji mobilnej</span>
    </div>
  );

  return (
    <div className="achievements-grid">
      {achievements.map(a => (
        <div key={a.id} className="achievement-card"
          style={{ borderColor: RARITY_COLOR[a.rarity] ?? '#ffffff20' }}>
          <div className="achievement-icon">{a.icon}</div>
          <div className="achievement-label">{a.label}</div>
          <div className="achievement-rarity" style={{ color: RARITY_COLOR[a.rarity] ?? '#fff' }}>
            {a.rarity?.toUpperCase()}
          </div>
          <div className="achievement-pts">+{a.points} pkt</div>
        </div>
      ))}
    </div>
  );
}

// ── Zakładka: Spoty ───────────────────────────────────────────────────────────
function SpotsTab({ userId, token }) {
  const [spots,   setSpots]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${API}/profile/${userId}/spots`, { headers })
      .then(r => r.json())
      .then(data => { setSpots(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [userId, token]);

  if (loading) return <div className="tab-loading"><div className="profile-spinner"></div></div>;

  if (spots.length === 0) return (
    <div className="profile-empty">
      <span className="material-icons">location_on</span>
      <p>Brak dodanych spotów</p>
      <span>Dodawaj spoty przez aplikację mobilną VROOM</span>
    </div>
  );

  return (
    <div className="spots-grid">
      {spots.map(spot => (
        <div key={spot.id} className="spot-card">
          {spot.photos?.[0]
            ? <img src={spot.photos[0]} alt={spot.name} className="spot-photo" />
            : <div className="spot-photo-placeholder"><span className="material-icons">location_on</span></div>
          }
          <div className="spot-info">
            <div className="spot-name">{spot.name}</div>
            <div className="spot-meta">
              <span><span className="material-icons">favorite</span>{spot.likesCount}</span>
              <span><span className="material-icons">chat_bubble</span>{spot.commentsCount}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Zakładka: Edycja ─────────────────────────────────────────────────────────
function EditTab({ profile, token, onSaved }) {
  const [username,  setUsername]  = useState(profile?.username  ?? '');
  const [bio,       setBio]       = useState(profile?.bio       ?? '');
  const [location,  setLocation]  = useState(profile?.location  ?? '');
  const [saving,    setSaving]    = useState(false);
  const [msg,       setMsg]       = useState(null);
  const [avatarPrev, setAvatarPrev] = useState(profile?.avatarUrl ?? null);
  const fileRef = useRef();

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarPrev(URL.createObjectURL(file));
    const form = new FormData();
    form.append('avatar', file);
    try {
      const res  = await fetch(`${API}/profile/avatar`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form,
      });
      const data = await res.json();
      if (res.ok) {
        setMsg({ type: 'success', text: '✅ Avatar zaktualizowany!' });
        onSaved({ avatarUrl: data.avatarUrl });
      } else {
        setMsg({ type: 'error', text: data.error || 'Błąd uploadu avatara' });
      }
    } catch {
      setMsg({ type: 'error', text: 'Błąd połączenia' });
    }
    setTimeout(() => setMsg(null), 4000);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg(null);
    try {
      const res  = await fetch(`${API}/profile/me`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, bio, location }),
      });
      const data = await res.json();
      if (res.ok) {
        setMsg({ type: 'success', text: '✅ Profil zapisany!' });
        onSaved(data);
      } else {
        setMsg({ type: 'error', text: data.error || 'Błąd zapisu' });
      }
    } catch {
      setMsg({ type: 'error', text: 'Błąd połączenia' });
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(null), 4000);
    }
  };

  return (
    <form className="edit-form" onSubmit={handleSave}>
      {/* Avatar */}
      <div className="edit-avatar-section">
        <div className="edit-avatar-preview" onClick={() => fileRef.current?.click()}>
          {avatarPrev
            ? <img src={avatarPrev} alt="avatar" />
            : <span>{profile?.username?.slice(0, 2).toUpperCase()}</span>
          }
          <div className="edit-avatar-overlay">
            <span className="material-icons">photo_camera</span>
          </div>
        </div>
        <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarChange} />
        <p className="edit-avatar-hint">Kliknij aby zmienić avatar</p>
      </div>

      <div className="edit-field">
        <label>NAZWA UŻYTKOWNIKA</label>
        <div className="edit-input-wrap">
          <span className="material-icons">person</span>
          <input type="text" value={username} onChange={e => setUsername(e.target.value)} maxLength={30} required />
        </div>
      </div>

      <div className="edit-field">
        <label>LOKALIZACJA</label>
        <div className="edit-input-wrap">
          <span className="material-icons">location_on</span>
          <input type="text" value={location} onChange={e => setLocation(e.target.value)} placeholder="np. Warszawa" maxLength={50} />
        </div>
      </div>

      <div className="edit-field">
        <label>BIO</label>
        <div className="edit-input-wrap edit-textarea-wrap">
          <span className="material-icons">edit_note</span>
          <textarea value={bio} onChange={e => setBio(e.target.value)} placeholder="Napisz coś o sobie..." maxLength={200} rows={3} />
        </div>
        <span className="edit-char-count">{bio.length}/200</span>
      </div>

      {msg && <div className={`edit-msg ${msg.type}`}>{msg.text}</div>}

      <button type="submit" className="btn-save-profile" disabled={saving}>
        {saving
          ? <><div className="btn-spinner"></div>Zapisywanie...</>
          : <><span className="material-icons">save</span>ZAPISZ ZMIANY</>
        }
      </button>
    </form>
  );
}

// ── GŁÓWNA STRONA PROFILU ─────────────────────────────────────────────────────
export default function ProfilePage({ user, onLogout }) {
  const navigate    = useNavigate();
  const [profile,   setProfile]   = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [activeTab, setActiveTab] = useState('stats');

  const token  = user?.token;
  const userId = user?.id ?? user?.userId;

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/profile/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      setProfile(mapProfile(await res.json()));
    } catch {
      setProfile(mapProfile(user));
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const handleProfileUpdate = (partial) => {
    setProfile(p => p ? { ...p, ...mapProfile({ ...p, ...partial }) } : p);
    const saved = sessionStorage.getItem('vroom_user');
    if (saved) {
      try {
        sessionStorage.setItem('vroom_user', JSON.stringify({ ...JSON.parse(saved), ...partial }));
      } catch {}
    }
  };

  const initials    = profile?.username?.slice(0, 2).toUpperCase() ?? '??';
  const joinedLabel = profile?.createdAt
    ? new Date(profile.createdAt).toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' })
    : '—';

  const TABS = [
    { id: 'betaTests',    icon: 'app_registration',            label: 'Beta testy' }, 
    { id: 'referral', icon: 'group_add', label: 'Zaproś' },
    { id: 'stats',        icon: 'bar_chart',      label: 'Statystyki' },
    { id: 'cars',         icon: 'directions_car',  label: 'Garaż' },
    { id: 'achievements', icon: 'military_tech',   label: 'Osiągnięcia' },
    { id: 'spots',        icon: 'location_on',     label: 'Spoty' },
    { id: 'edit',         icon: 'edit',            label: 'Edytuj' },
    
  ];

  if (loading && !profile) return (
    <div className="profile-loading">
      <div className="profile-spinner"></div>
      <p>Ładowanie profilu...</p>
    </div>
  );

  return (
    <div className="profile-page">
      <div className="bg-grid-overlay"></div>

      {/* TOPBAR */}
      <div className="profile-topbar">
        <div className="logo" style={{ cursor: 'pointer' }} onClick={() => navigate('/')}>
          <span className="material-icons">speed</span>
          VROOM<span className="dot">.</span>APP
        </div>
        <div className="profile-topbar-right">
          <button className="btn-back-landing" onClick={() => navigate('/')}>
            <span className="material-icons">home</span>
            Landing
          </button>
          <button className="btn-logout" onClick={() => { onLogout(); navigate('/'); }}>
            <span className="material-icons">logout</span>
            Wyloguj
          </button>
        </div>
      </div>

      <div className="profile-content">

        {/* HERO CARD */}
        <div className="profile-hero-card">
          <div className="profile-avatar">
            {profile?.avatarUrl
              ? <img src={profile.avatarUrl} alt="avatar" />
              : <span>{initials}</span>
            }
          </div>
          <div className="profile-hero-info">
            <h1 className="profile-username">{profile?.username ?? '—'}</h1>
            {profile?.location && (
              <p className="profile-location">
                <span className="material-icons">location_on</span>{profile.location}
              </p>
            )}
            <p className="profile-joined">
              <span className="material-icons">calendar_today</span>Dołączył: {joinedLabel}
            </p>
            {profile?.bio && <p className="profile-bio">{profile.bio}</p>}
            <div className="profile-hero-badges">
              {profile?.position && (
                <span className="profile-badge">🏆 #{profile.position} globalnie</span>
              )}
              {profile?.streak > 0 && (
                <span className="profile-badge">🔥 {profile.streak} dni streak</span>
              )}
              {profile?.mainCar && (
                <span className="profile-badge">🚗 {profile.mainCar.brand}</span>
              )}
            </div>
          </div>
        </div>

        {/* SZYBKIE STATY */}
        <div className="profile-stats-row">
          {[
            { icon: 'speed',        label: 'KILOMETRY', value: `${Number(profile?.totalDistance ?? 0).toFixed(0)} km` },
            { icon: 'emoji_events', label: 'RANKING',   value: profile?.position ? `#${profile.position}` : '—' },
            { icon: 'location_on',  label: 'MIASTA',    value: String(profile?.cityCount ?? 0) },
            { icon: 'star',         label: 'PUNKTY',    value: (profile?.points ?? 0).toLocaleString('pl-PL') },
          ].map(s => (
            <div key={s.label} className="profile-stat-box">
              <span className="material-icons">{s.icon}</span>
              <div className="profile-stat-value">{s.value}</div>
              <div className="profile-stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        {/* TABS */}
        <div className="profile-tabs">
          {TABS.map(t => (
            <button
              key={t.id}
              className={`profile-tab ${activeTab === t.id ? 'active' : ''}`}
              onClick={() => setActiveTab(t.id)}
            >
              <span className="material-icons">{t.icon}</span>
              <span className="profile-tab-label">{t.label}</span>
            </button>
          ))}
        </div>

        {/* CONTENT */}
        <div className="profile-tab-content">
          {activeTab === 'stats'        && <StatsTab        profile={profile} />}
          {activeTab === 'referral' && <ReferralTab token={token} />}
          {activeTab === 'cars'         && <CarsTab         userId={userId} token={token} />}
          {activeTab === 'achievements' && <AchievementsTab userId={userId} />}
          {activeTab === 'spots'        && <SpotsTab        userId={userId} token={token} />}
          {activeTab === 'edit'         && <EditTab         profile={profile} token={token} onSaved={handleProfileUpdate} />}
          {activeTab === 'betaTests' && <BetaSignupSection />}
        </div>

      </div>
    </div>
  );
}